import type { ShellRailDescriptor } from "./shell-rail-geometry";

export type ShellRailSide = "left" | "right";
export type ShellRailSpot = "sidebar-surface" | "strategium-slot";
export type ShellRailVisualSurface = "sidebar" | "strategium";

export type RightShellRailId =
  | "strategium"
  | "narrativeflow-research"
  | "consilium-desk"
  | "econ-desk-plan-queue"
  | "settings-quick-access"
  | "war-room"
  | "forum"
  | "daily-performance"
  | "narrativeflow-deskmap-research"
  | "narrativeflow-weekly-summary"
  | "cao-chat";

export type LeftShellRailId =
  | "left-underlay"
  | "cao-conversation-history"
  | "narrativeflow-session-history";

export type ShellRailId = RightShellRailId | LeftShellRailId;

export interface ShellRailRegistration {
  id: ShellRailId;
  side: ShellRailSide;
  spot: ShellRailSpot;
  visualSurface: ShellRailVisualSurface;
  defaultWidth: string;
  behavior: "push";
}

const STRATEGIUM_WIDTH = "min(380px, 42vw)";
const SIDEBAR_UNDERLAY_WIDTH = "min(320px, 32vw)";

const RAIL_REGISTRY = {
  strategium: rightRail("strategium", STRATEGIUM_WIDTH),
  "narrativeflow-research": rightRail(
    "narrativeflow-research",
    "clamp(320px, 34vw, 520px)",
  ),
  "consilium-desk": rightRail("consilium-desk", STRATEGIUM_WIDTH),
  "econ-desk-plan-queue": rightRail("econ-desk-plan-queue", STRATEGIUM_WIDTH),
  "settings-quick-access": rightRail(
    "settings-quick-access",
    "clamp(340px, 34vw, 440px)",
  ),
  "war-room": rightRail("war-room", STRATEGIUM_WIDTH),
  forum: rightRail("forum", STRATEGIUM_WIDTH),
  "daily-performance": rightRail("daily-performance", STRATEGIUM_WIDTH),
  "narrativeflow-deskmap-research": rightRail(
    "narrativeflow-deskmap-research",
    "clamp(320px, 34vw, 520px)",
  ),
  "narrativeflow-weekly-summary": rightRail(
    "narrativeflow-weekly-summary",
    STRATEGIUM_WIDTH,
  ),
  "cao-chat": rightRail("cao-chat", "min(420px, 42vw)"),
  "left-underlay": leftRail("left-underlay", SIDEBAR_UNDERLAY_WIDTH),
  "cao-conversation-history": leftRail(
    "cao-conversation-history",
    SIDEBAR_UNDERLAY_WIDTH,
  ),
  "narrativeflow-session-history": leftRail(
    "narrativeflow-session-history",
    SIDEBAR_UNDERLAY_WIDTH,
  ),
} satisfies Record<ShellRailId, ShellRailRegistration>;

function rightRail(
  id: RightShellRailId,
  defaultWidth: string,
): ShellRailRegistration {
  return {
    id,
    side: "right",
    spot: "strategium-slot",
    visualSurface: "strategium",
    defaultWidth,
    behavior: "push",
  };
}

function leftRail(
  id: LeftShellRailId,
  defaultWidth: string,
): ShellRailRegistration {
  return {
    id,
    side: "left",
    spot: "sidebar-surface",
    visualSurface: "sidebar",
    defaultWidth,
    behavior: "push",
  };
}

export function getShellRailRegistration(
  id: ShellRailId,
): ShellRailRegistration {
  return RAIL_REGISTRY[id];
}

export function shellRailDescriptorFor(
  id: ShellRailId,
  isVisible: boolean,
  width?: string,
): ShellRailDescriptor {
  const rail = getShellRailRegistration(id);
  return {
    id,
    side: rail.side,
    isVisible,
    width: width ?? rail.defaultWidth,
    behavior: rail.behavior,
  };
}

export function isStrategiumSpotRail(id: ShellRailId): boolean {
  return getShellRailRegistration(id).spot === "strategium-slot";
}

export function isSidebarSurfaceRail(id: ShellRailId): boolean {
  return getShellRailRegistration(id).spot === "sidebar-surface";
}

export function listShellRailRegistrations(
  side?: ShellRailSide,
): ShellRailRegistration[] {
  const rails = Object.values(RAIL_REGISTRY);
  return side ? rails.filter((rail) => rail.side === side) : rails;
}
